import React, { forwardRef, ForwardedRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classnames from 'classnames/bind'
import {
  colorVars,
  colorLongToShort,
  getCssColorFromColorName,
} from '../../../utils/colors'
import { ColorNames } from '../../../constants'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const SIZES_MAP = {
  mini: 12,
  tiny: 14,
  small: 16,
  large: 18,
  big: 20,
  huge: 24,
  massive: 30,
  unset: 'unset',
}
export const SIZES = _.keys(SIZES_MAP)
const WEIGHTS_MAP = {
  normal: 400,
  medium: 600,
  heavy: 700,
  black: 900,
}
export const WEIGHTS = _.keys(WEIGHTS_MAP)
export const COLOR_NAMES = _.keys(colorVars).map(colorLongToShort)

export interface TextProps {
  as?: React.ElementType
  children?: React.ReactNode
  className?: string
  color?: string | ColorNames | 'unset' | 'inherit'
  content?: React.ReactNode
  size?: keyof typeof SIZES_MAP
  weight?: keyof typeof WEIGHTS_MAP
  elmId?: string
  style?: React.CSSProperties
  compact?: boolean
  ref?: ForwardedRef<HTMLElement>
}

const Text = forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {
    as: Component = 'div',
    children,
    className,
    color = 'grayDark',
    content,
    size = 'small',
    weight = 'normal',
    elmId,
    style,
    ...rest
  } = props

  const newStyle = {
    color: getCssColorFromColorName(color as ColorNames),
    fontSize: SIZES_MAP[size],
    fontWeight: WEIGHTS_MAP[weight],
  }

  return (
    <Component
      ref={ref}
      data-elm-id={elmId}
      className={cx('text', className)}
      style={{ ...newStyle, ...style }}
      {...rest}
    >
      {content || children}
    </Component>
  )
})

// HEADER
const HEADER_LEVELS_MAP: {
  [key: number]: { as: React.ElementType; size: keyof typeof SIZES_MAP }
} = {
  1: { as: 'h1', size: 'massive' },
  2: { as: 'h2', size: 'huge' },
  3: { as: 'h3', size: 'big' },
}
export const HEADER_LEVELS = _.keys(HEADER_LEVELS_MAP)

export type TextHeaderProps = Pick<
  TextProps,
  'className' | 'children' | 'as' | 'color' | 'ref' | 'style' | 'elmId'
> & {
  level?: keyof typeof HEADER_LEVELS_MAP
  compact?: boolean
}

const TextHeader = forwardRef<HTMLElement, TextHeaderProps>(
  ({ className, level = 1, compact = false, as, ...rest }, ref) => {
    return (
      <Text
        ref={ref}
        weight='heavy'
        className={cx(className, `header-${level}`, { compact })}
        size={HEADER_LEVELS_MAP[level]?.size}
        as={as || HEADER_LEVELS_MAP[level]?.as}
        {...rest}
      />
    )
  }
)

interface TextHeaderStatics {
  getSizeInPixels: (level: keyof typeof HEADER_LEVELS_MAP) => number | string
}

const TextHeaderWithStatics: React.FC<TextHeaderProps> & TextHeaderStatics =
  TextHeader as unknown as React.FC<TextHeaderProps> & TextHeaderStatics

TextHeaderWithStatics.getSizeInPixels = (
  level: keyof typeof HEADER_LEVELS_MAP
): number | string => SIZES_MAP[HEADER_LEVELS_MAP[level].size]

const BODY_SIZE_MAP = {
  large: 'large',
  normal: 'small',
  small: 'tiny',
  extraSmall: 'mini',
}

export interface TextBodyProps extends Omit<TextProps, 'size'> {
  size?: keyof typeof BODY_SIZE_MAP
}

const TextBody = forwardRef<HTMLElement, TextBodyProps>(
  ({ size = 'normal', ...rest }, ref) => (
    <Text
      {...rest}
      size={BODY_SIZE_MAP[size] as keyof typeof SIZES_MAP}
      ref={ref}
    />
  )
)

interface TextBodyStatics {
  getSizeInPixels: (size: keyof typeof BODY_SIZE_MAP) => number | string
}

const TextBodyWithStatics: React.FC<TextBodyProps> & TextBodyStatics =
  TextBody as unknown as React.FC<TextBodyProps> & TextBodyStatics

TextBodyWithStatics.getSizeInPixels = (
  size: keyof typeof BODY_SIZE_MAP
): number | string => SIZES_MAP[BODY_SIZE_MAP[size] as keyof typeof SIZES_MAP]

// attach Body and Header to Text
const textPropTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf(['', ...COLOR_NAMES, 'unset', 'inherit']),
  content: PropTypes.node,
  size: PropTypes.oneOf(SIZES),
  weight: PropTypes.oneOf(WEIGHTS),
  elmId: PropTypes.string,
  style: PropTypes.object,
}

interface TextStatics {
  Body: typeof TextBodyWithStatics
  Header: typeof TextHeaderWithStatics
  getWeightInNumericalTerms: (weight: keyof typeof WEIGHTS_MAP) => number
  getSizeInPixels: (size: keyof typeof SIZES_MAP) => number | string
  propTypes: typeof textPropTypes
}

const TextWithStatics: React.FC<TextProps> & TextStatics =
  Text as unknown as React.FC<TextProps> & TextStatics

TextWithStatics.Body = TextBodyWithStatics
TextWithStatics.Header = TextHeaderWithStatics
// @ts-ignore next
TextWithStatics.propTypes = textPropTypes
TextWithStatics.getWeightInNumericalTerms = weight => WEIGHTS_MAP[weight]
TextWithStatics.getSizeInPixels = size => SIZES_MAP[size]

export default TextWithStatics
